import React, { useState } from 'react';
import { Typography, Select, Switch } from 'antd';
import { message } from '../../../services/toastMessage';
import { useTranslation } from 'react-i18next';
import { useUserProfile } from '../../../contextProviders/UserProfileProvider';
import { updateMyUserProfile } from '../../../services/UserProfileService';
import { NotificationChannel } from '../../../interfaces/heavyDutyMachinery/IUserProfile';
import SettingsCard from '../../../components/Settings/SettingsCard';
import SettingsRowShell from '../../../components/Settings/SettingsRowShell';
import { useAppBreakpoint } from '../../../hooks/useAppBreakpoint';
import { TRANSLATIONS } from '../../../constants/translations';
import { translate } from '../../../helpers/translationHelper';
import { usePushNotifications } from '../../../hooks/usePushNotifications';
import { CORAL, CORAL_SOFT_BG } from '../../../theme/creamyTokens';

const { Title, Text } = Typography;

type ChannelField =
  | 'notifyBookingConfirmationChannel'
  | 'notifyChatMessageChannel'
  | 'notifyBookingReminderChannel'
  | 'notifyPlatformNewsChannel';

const CATEGORIES: { field: ChannelField; titleKey: string; descriptionKey: string }[] = [
  {
    field: 'notifyBookingConfirmationChannel',
    titleKey: TRANSLATIONS.SETTINGS_NOTIF_BOOKING_CONFIRMATION_TITLE,
    descriptionKey: TRANSLATIONS.SETTINGS_NOTIF_BOOKING_CONFIRMATION_DESCRIPTION,
  },
  {
    field: 'notifyChatMessageChannel',
    titleKey: TRANSLATIONS.SETTINGS_NOTIF_CHAT_MESSAGE_TITLE,
    descriptionKey: TRANSLATIONS.SETTINGS_NOTIF_CHAT_MESSAGE_DESCRIPTION,
  },
  {
    field: 'notifyBookingReminderChannel',
    titleKey: TRANSLATIONS.SETTINGS_NOTIF_BOOKING_REMINDER_TITLE,
    descriptionKey: TRANSLATIONS.SETTINGS_NOTIF_BOOKING_REMINDER_DESCRIPTION,
  },
  {
    field: 'notifyPlatformNewsChannel',
    titleKey: TRANSLATIONS.SETTINGS_NOTIF_PLATFORM_NEWS_TITLE,
    descriptionKey: TRANSLATIONS.SETTINGS_NOTIF_PLATFORM_NEWS_DESCRIPTION,
  },
];

/** "Configuración > Notificaciones" -- one Email/Push/Ninguna channel selector per HeavyDutyMachinery-specific category, persisted on UserProfile. */
const NotificationsSection: React.FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useAppBreakpoint();
  const { userProfile, refresh } = useUserProfile();
  const push = usePushNotifications();
  const [savingField, setSavingField] = useState<ChannelField | null>(null);

  const handleChange = async (field: ChannelField, value: NotificationChannel) => {
    setSavingField(field);
    try {
      await updateMyUserProfile({ [field]: value });
      await refresh();
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.PROFILE_UPDATED_MESSAGE));
    } finally {
      setSavingField(null);
    }
  };

  // Whichever of these is true is the ONLY one shown: they are ordered by what
  // the person can actually do about it, most actionable first.
  const pushNotice = !push.supported
    ? translate(t, TRANSLATIONS.PUSH_DEVICE_UNSUPPORTED)
    : push.needsHomeScreenInstall
      ? translate(t, TRANSLATIONS.PUSH_DEVICE_INSTALL_FIRST)
      : push.permission === 'denied'
        ? translate(t, TRANSLATIONS.PUSH_DEVICE_BLOCKED)
        : push.error === 'PushNotConfigured'
          ? translate(t, TRANSLATIONS.PUSH_DEVICE_NOT_CONFIGURED)
          : push.error
            ? translate(t, TRANSLATIONS.PUSH_DEVICE_ERROR)
            : null;

  const channelOptions = [
    { value: 'Email', label: translate(t, TRANSLATIONS.SETTINGS_NOTIF_CHANNEL_EMAIL) },
    { value: 'Push', label: translate(t, TRANSLATIONS.SETTINGS_NOTIF_CHANNEL_PUSH) },
    { value: 'None', label: translate(t, TRANSLATIONS.SETTINGS_NOTIF_CHANNEL_NONE) },
  ];

  return (
    <div>
      {/* Redundant on mobile -- the list->detail header (Settings.tsx) already shows this section's name. */}
      {!isMobile && (
        <Title level={4} style={{ marginBottom: 20 }}>
          {translate(t, TRANSLATIONS.SETTINGS_NAV_NOTIFICATIONS)}
        </Title>
      )}

      {/* Above the four categories on purpose, because it is the switch that
          makes "Push" below mean anything at all: choosing Push for a category
          while this is off is choosing a channel nobody is listening on.
          It is also the only setting on this page that is per DEVICE -- browser
          permission does not travel with the account -- which the description
          says out loud rather than leaving somebody to wonder why their laptop
          is silent. */}
      <SettingsCard>
        <SettingsRowShell
          left={
            <div>
              <Text strong>{translate(t, TRANSLATIONS.PUSH_DEVICE_TITLE)}</Text>
              <div>
                <Text type="secondary">{translate(t, TRANSLATIONS.PUSH_DEVICE_DESCRIPTION)}</Text>
              </div>
              {pushNotice && (
                <div
                  style={{
                    marginTop: 8,
                    padding: '8px 12px',
                    borderRadius: 12,
                    background: CORAL_SOFT_BG,
                    color: CORAL,
                    fontSize: 12,
                  }}
                >
                  {pushNotice}
                </div>
              )}
            </div>
          }
          right={
            <Switch
              checked={push.enabled}
              loading={push.busy}
              // Nothing to toggle when the browser cannot do it, or when the
              // person has blocked it: no site can un-block itself, so a switch
              // that looked live would be a switch that does nothing.
              disabled={!push.supported || push.permission === 'denied' || push.needsHomeScreenInstall}
              onChange={(checked) => (checked ? push.enable() : push.disable())}
            />
          }
        />
      </SettingsCard>

      <SettingsCard noMarginBottom>
        {CATEGORIES.map((category) => (
          <SettingsRowShell
            key={category.field}
            left={
              <div>
                <Text strong>{translate(t, category.titleKey)}</Text>
                <div>
                  <Text type="secondary">{translate(t, category.descriptionKey)}</Text>
                </div>
              </div>
            }
            right={
              <Select
                style={{ width: 140 }}
                value={userProfile?.[category.field] ?? 'Email'}
                loading={savingField === category.field}
                onChange={(value) => handleChange(category.field, value)}
                options={channelOptions}
              />
            }
          />
        ))}
      </SettingsCard>
    </div>
  );
};

export default NotificationsSection;
